import React, { useEffect, useState, useMemo } from 'react';
import { Button, Input, Select } from '..';
import {
  FiTruck,
  FiHash,
  FiCalendar,
  FiDroplet,
  FiFileText,
  FiCheckCircle,
  FiBriefcase,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import '../CompanyForm/style.css';

export default function VehicleForm({ companyId, companies = [], initialData, onSuccess, onCancel }) {
  const isEdit = !!initialData;
  const [loading, setLoading] = useState(false);
  const [serviceStations, setServiceStations] = useState([]);

  const [formData, setFormData] = useState({
    company_id: '',
    service_station_id: '',
    plate: '',
    model: '',
    brand: '',
    year: '',
    color: '',
    renavam: '',
    active: true,
  });

  useEffect(() => {
    if (initialData) {
      const initialCompanyId = initialData.company_id || initialData.company?.id;
      setFormData({
        company_id: initialCompanyId ? String(initialCompanyId) : '',
        service_station_id: initialData.service_station_id ? String(initialData.service_station_id) : (initialData.serviceStation?.id ? String(initialData.serviceStation.id) : ''),
        plate: initialData.plate || '',
        model: initialData.model || '',
        brand: initialData.brand || '',
        year: initialData.year || '',
        color: initialData.color || '',
        renavam: initialData.renavam || '',
        active: initialData.active ?? true,
      });
    } else if (companyId) {
        setFormData(prev => ({ ...prev, company_id: String(companyId) }));
    }
  }, [initialData, companyId]);

  useEffect(() => {
    if (formData.company_id) {
      loadServiceStations(formData.company_id);
    } else {
      setServiceStations([]);
    }
  }, [formData.company_id]);

  async function loadServiceStations(companyId) {
    try {
      const { data } = await Api.get(`/companies/${companyId}/stations`);
      setServiceStations(data || []);
    } catch (error) {
      console.error('Error loading service stations:', error);
    }
  }

  const companyOptions = useMemo(() => {
    return companies.map(c => ({
      value: String(c.id),
      label: c.corporate_name || c.trade_name || `Empresa ${c.id}`,
    }));
  }, [companies]);

  function handleChange(event) {
    const { id, value } = event.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.company_id) {
        toast.error('Empresa é obrigatória.');
        return;
    }

    if (!formData.plate || !formData.model) {
      toast.error('Placa e Modelo são obrigatórios.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        ...formData,
        plate: formData.plate.toUpperCase(),
      };

      const targetCompanyId = formData.company_id;

      if (isEdit) {
        // Use the original company ID for the URL to find the vehicle
        const originalCompanyId = initialData.company_id || initialData.company?.id || companyId;
        await Api.put(`/companies/${originalCompanyId}/vehicles/${initialData.id}`, payload);
        toast.success('Veículo atualizado com sucesso!');
      } else {
        await Api.post(`/companies/${targetCompanyId}/vehicles`, payload);
        toast.success('Veículo criado com sucesso!');
      }

      if (onSuccess) onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || 'Erro ao salvar veículo.');
    } finally {
      setLoading(false);
    }
  }

  const carBrands = [
    { value: 'Volkswagen', label: 'Volkswagen' },
    { value: 'Chevrolet', label: 'Chevrolet' },
    { value: 'Fiat', label: 'Fiat' },
    { value: 'Toyota', label: 'Toyota' },
    { value: 'Hyundai', label: 'Hyundai' },
    { value: 'Ford', label: 'Ford' },
    { value: 'Renault', label: 'Renault' },
    { value: 'Honda', label: 'Honda' },
    { value: 'Nissan', label: 'Nissan' },
    { value: 'Jeep', label: 'Jeep' },
    { value: 'Mitsubishi', label: 'Mitsubishi' },
    { value: 'Citroën', label: 'Citroën' },
    { value: 'Peugeot', label: 'Peugeot' },
    { value: 'Kia', label: 'Kia' },
    { value: 'Mercedes-Benz', label: 'Mercedes-Benz' },
    { value: 'BMW', label: 'BMW' },
    { value: 'Audi', label: 'Audi' },
    { value: 'Volvo', label: 'Volvo' },
    { value: 'Land Rover', label: 'Land Rover' },
    { value: 'Chery', label: 'Chery' },
    { value: 'BYD', label: 'BYD' },
    { value: 'GWM', label: 'GWM' },
    { value: 'Suzuki', label: 'Suzuki' },
    { value: 'JAC', label: 'JAC' },
    { value: 'Troller', label: 'Troller' },
    { value: 'Shineray', label: 'Shineray' },
  ];

  return (
    <form className="company-form-component" onSubmit={handleSubmit}>
      <div className="form-body">
        <h3 className="section-title" style={{ marginTop: '0px' }}>
          <FiTruck style={{ marginRight: '8px' }} />
          Dados do Veículo
        </h3>

        <div className="form-grid">
          <Select
            id="company_id"
            label="Empresa *"
            placeholder="SELECIONE UMA EMPRESA..."
            value={formData.company_id}
            onChange={handleChange}
            icon={FiBriefcase}
            options={companyOptions}
            searchable
          />

          <Select
            id="service_station_id"
            label="Posto de Serviço"
            placeholder="Selecione o Posto"
            value={formData.service_station_id}
            onChange={handleChange}
            icon={FiTruck}
            options={serviceStations.map(s => ({
              value: String(s.id),
              label: s.name || s.trade_name || `Posto ${s.id}`
            }))}
            searchable
            disabled={!formData.company_id}
          />

          <Input
            id="plate"
            label="Placa *"
            placeholder="ABC-1234"
            value={formData.plate}
            onChange={handleChange}
            icon={FiHash}
            mask="plate"
          />

          <Input
            id="model"
            label="Modelo *"
            placeholder="Ex: Gol 1.6"
            value={formData.model}
            onChange={handleChange}
            icon={FiTruck}
          />

          <Select
            id="brand"
            label="Marca"
            placeholder="Selecione a Marca"
            value={formData.brand}
            onChange={handleChange}
            icon={FiTruck}
            options={carBrands}
            searchable
          />

          <Input
            id="year"
            label="Ano"
            placeholder="Ex: 2023"
            value={formData.year}
            onChange={handleChange}
            icon={FiCalendar}
            mask="year"
          />

          <Input
            id="color"
            label="Cor"
            placeholder="Ex: Branco"
            value={formData.color}
            onChange={handleChange}
            icon={FiDroplet}
          />

          <Input
            id="renavam"
            label="Renavam"
            placeholder="Código Renavam"
            value={formData.renavam}
            onChange={handleChange}
            icon={FiFileText}
            mask="renavam"
          />
          
            <Select
            id="active"
            label="Status"
            value={formData.active ? 'true' : 'false'}
            onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.value === 'true' }))}
            icon={FiCheckCircle}
            options={[
              { value: 'true', label: 'Ativo' },
              { value: 'false', label: 'Inativo' },
            ]}
          />
        </div>
      </div>

      <div className="form-actions">
        <Button
          type="button"
          className="cancel-button"
          onClick={onCancel}
          disabled={loading}
        >
          Cancelar
        </Button>
        <Button type="submit" loading={loading}>
          Salvar
        </Button>
      </div>
    </form>
  );
}
